import { Modal, Text, Progress, Flex, Title, Loader } from "@mantine/core";
import { useEffect, useState } from "react";

interface UpdateInfo {
  version: string;
  message: string;
}

interface ProgressInfo {
  percent: number;
  transferred: number;
  total: number;
  message?: string;
}

interface AppAccessInfo {
  canUseApp: boolean;
  updateAvailable: boolean;
  updateDownloaded: boolean;
}

const UpdateBlocker = () => {
  const [updateRequired, setUpdateRequired] = useState<UpdateInfo | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<ProgressInfo | null>(
    null,
  );
  const [appAccess, setAppAccess] = useState<AppAccessInfo>({
    canUseApp: true,
    updateAvailable: false,
    updateDownloaded: false,
  });
  const [isElectron, setIsElectron] = useState(false);

  // Development mode detection
  const isDevelopment =
    import.meta.env.DEV || window.location.hostname === "localhost";

  useEffect(() => {
    // Check if we're in Electron environment
    const electronCheck = !!window?.api;
    setIsElectron(electronCheck);

    if (!electronCheck) {
      // In web browser, always allow app usage
      setAppAccess({
        canUseApp: true,
        updateAvailable: false,
        updateDownloaded: false,
      });
      return;
    }

    try {
      // Check if app can be used on mount
      if (window.api?.checkAppAccess) {
        window.api.checkAppAccess((accessInfo: AppAccessInfo) => {
          setAppAccess(accessInfo);
        });
      }

      // Listen for update required events
      if (window.api?.onUpdateRequired) {
        window.api.onUpdateRequired((updateInfo: UpdateInfo) => {
          setUpdateRequired(updateInfo);
          setAppAccess(prev => ({
            ...prev,
            canUseApp: false,
            updateAvailable: true,
          }));
        });
      }

      // Listen for download progress
      if (window.api?.onUpdateProgress) {
        window.api.onUpdateProgress((progress: ProgressInfo) => {
          setDownloadProgress(progress);
        });
      }
    } catch (error) {
      console.warn("UpdateBlocker: Failed to set up update listeners:", error);
      // Ensure app can be used if there's an error setting up listeners
      setAppAccess({
        canUseApp: true,
        updateAvailable: false,
        updateDownloaded: false,
      });
    }

    // Periodic check for app access (every 10 seconds)
    const interval = setInterval(() => {
      if (window?.api?.checkAppAccess) {
        window.api.checkAppAccess((accessInfo: AppAccessInfo) => {
          setAppAccess(accessInfo);

          // If update is downloaded, clear progress and show ready message
          if (accessInfo.updateDownloaded) {
            setDownloadProgress(null);
          }
        });
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Never block in development mode
  if (isDevelopment) return null;

  // Only block app usage in Electron environment and if update is actually required
  const shouldBlockApp =
    isElectron &&
    (!appAccess.canUseApp ||
      (appAccess.updateAvailable && !appAccess.updateDownloaded));

  // In web browser or if no blocking needed, don't render anything
  if (!shouldBlockApp) return null;

  const getModalContent = () => {
    if (downloadProgress) {
      return (
        <>
          <Title order={3} mb="md" ta="center">
            Downloading Required Update
          </Title>
          <Text ta="center" mb="lg">
            {downloadProgress.message ||
              `Downloading update... ${downloadProgress.percent}%`}
          </Text>
          <Progress
            value={downloadProgress.percent}
            size="lg"
            radius="md"
            mb="md"
            animate
          />
          <Text ta="center" size="sm" c="dimmed">
            {formatBytes(downloadProgress.transferred)} /{" "}
            {formatBytes(downloadProgress.total)}
          </Text>
          <Text ta="center" size="sm" mt="md">
            Please wait while the update downloads. The app will restart
            automatically when ready.
          </Text>
        </>
      );
    }

    if (appAccess.updateDownloaded) {
      return (
        <>
          <Title order={3} mb="md" ta="center">
            Update Ready
          </Title>
          <Flex justify="center" mb="lg">
            <Loader size="lg" />
          </Flex>
          <Text ta="center" mb="md">
            The update has been downloaded and is ready to install.
          </Text>
          <Text ta="center" size="sm">
            The application will restart automatically to complete the update.
          </Text>
        </>
      );
    }

    return (
      <>
        <Title order={3} mb="md" ta="center">
          Update Required
        </Title>
        <Text ta="center" mb="lg">
          {updateRequired?.message ||
            "A new version is required to continue using the app."}
        </Text>
        <Flex justify="center" mb="lg">
          <Loader size="lg" />
        </Flex>
        <Text ta="center" size="sm">
          Please wait while the update is being prepared...
        </Text>
      </>
    );
  };

  return (
    <Modal
      opened={shouldBlockApp}
      onClose={() => {}} // Prevent closing
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      size="md"
      styles={{
        body: {
          padding: "2rem",
        },
      }}>
      {getModalContent()}
    </Modal>
  );
};

// Helper function to format bytes
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export default UpdateBlocker;
